import { useState, useRef, useCallback, useEffect } from "react";
import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import type { paths } from "../../api/generated/api-types";
import { logger } from "../../observability/LoggerFactory";

type ListRemovalsResponse =
  paths["/competitor-link-removals"]["get"]["responses"]["200"]["content"]["application/json"];

export type RemovalRunListItem = ListRemovalsResponse["items"][number];

type ScreenState =
  | { kind: "loading" }
  | { kind: "empty" }
  | { kind: "data"; items: RemovalRunListItem[]; totalCount: number }
  | { kind: "error"; message: string };

const DEFAULT_PAGE_SIZE = 25;

// Fixed Russian user-facing copy (frontend-language / frontend.interface-copy).
// The raw `HTTP <status>` / thrown error goes to the logger only, never to the UI.
const LIST_ERROR_MESSAGE = "Не удалось загрузить список прогонов. Попробуйте ещё раз.";

export interface UseCompetitorLinkRemovalListResult {
  state: ScreenState;
  fetching: boolean;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  refetch: () => void;
}

export function useCompetitorLinkRemovalList(): UseCompetitorLinkRemovalListResult {
  const [state, setState] = useState<ScreenState>({ kind: "loading" });
  const [fetching, setFetching] = useState(false);
  const [page, setPageState] = useState(1);
  const [pageSize, setPageSizeState] = useState(DEFAULT_PAGE_SIZE);
  const abortRef = useRef<AbortController | null>(null);
  const initialLoadDone = useRef(false);

  const fetchRuns = useCallback(async (currentPage: number, currentPageSize: number) => {
    if (abortRef.current !== null) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    if (!initialLoadDone.current) {
      setState({ kind: "loading" });
    }

    setFetching(true);

    const offset = (currentPage - 1) * currentPageSize;
    const parameters = new URLSearchParams();
    parameters.set("limit", String(currentPageSize));
    parameters.set("offset", String(offset));

    try {
      const response = await fetchWithRefresh(`/api/competitor-link-removals?${parameters.toString()}`, {
        signal: controller.signal,
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = (await response.json()) as ListRemovalsResponse;

      initialLoadDone.current = true;

      if (data.items.length === 0 && data.totalCount === 0) {
        setState({ kind: "empty" });
      } else {
        setState({ kind: "data", items: data.items, totalCount: data.totalCount });
      }
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      const detail = error instanceof Error ? error.message : String(error);
      logger.error({ err: detail }, "Failed to fetch competitor link removals");
      setState({ kind: "error", message: LIST_ERROR_MESSAGE });
    } finally {
      if (!controller.signal.aborted) {
        setFetching(false);
      }
    }
  }, []);

  const pageRef = useRef(page);
  pageRef.current = page;
  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;

  useEffect(() => {
    void fetchRuns(page, pageSize);
    return () => {
      if (abortRef.current !== null) {
        abortRef.current.abort();
      }
    };
  }, [page, pageSize, fetchRuns]);

  const refetch = useCallback(() => {
    void fetchRuns(pageRef.current, pageSizeRef.current);
  }, [fetchRuns]);

  const setPage = useCallback((newPage: number) => {
    setPageState(newPage);
  }, []);

  const setPageSize = useCallback((newPageSize: number) => {
    setPageSizeState(newPageSize);
    setPageState(1);
  }, []);

  return { state, fetching, page, pageSize, setPage, setPageSize, refetch };
}
